/**
 * W11 — the poll rules the bubble and the create dialog ask: whether an option row can be voted on,
 * which answers are the caller's own, what the bars are made of, and what a draft is still missing.
 * One pure function per decision, the same contract as `lib/chat.ts`'s doors and `lib/mentions.ts`'s
 * scans: nothing re-derives them at a call site.
 */
import type { MessageView, PollOptionView, PollView } from '@olmt/shared'
import type { MessageKey } from './i18n'

/** The api's bounds (`apps/api/src/messages.ts`): a draft outside them is a 400. */
export const POLL_MIN_OPTIONS = 2
export const POLL_MAX_OPTIONS = 10
export const POLL_OPTION_MAX_CHARS = 80

/** What the create dialog fills in: the question, its options, and the multiple-choice toggle. */
export type PollDraft = { question: string; options: string[]; multiple: boolean }

/**
 * Whether this message's options are a control right now. Three ways of not being one:
 *
 * - a **tombstone** (`deletedAt`) — the api answers 409 and "a message nobody can read" is not a
 *   place to answer either;
 * - an **optimistic copy** (`pending`) — its `pending:` id is one the server has never seen, so its
 *   `PUT` would 404, the same rule that keeps the write doors off it (`lib/chat.ts`);
 * - a message that is **not a poll** — or a `poll`-kind row whose payload is missing (a forwarded
 *   copy: the api copies the kind, not the options) — has nothing to answer, and the api's 400 is
 *   the same answer.
 */
export function canVote(message: MessageView): boolean {
  return (
    message.kind === 'poll' &&
    message.poll !== null &&
    message.deletedAt === null &&
    !message.pending
  )
}

/**
 * Whether this option is one of the caller's own answers. Per option, not per message: a
 * multiple-choice poll may hold several of them, so this is the question each row asks about
 * itself. Nobody has answered on the viewer with no session.
 */
export function choseOption(option: PollOptionView, meId: string | null): boolean {
  return meId !== null && option.voterIds.includes(meId)
}

/**
 * The option index this member chose, or null. A single-choice poll holds at most one answer (the
 * api replaces it), which is what every row compares against; a multiple-choice one may hold
 * several, and this names the first in the api's order — the rows ask `choseOption` about
 * themselves there.
 */
export function myVote(message: MessageView, meId: string | null): number | null {
  if (!meId) return null
  return message.poll?.options.find((option) => option.voterIds.includes(meId))?.index ?? null
}

/** What a poll's bars are drawn from. `perOption` is keyed by `option.index`, not by position. */
export type VoteTally = { total: number; perOption: number[] }

/**
 * The tally the rows and the footer read. `perOption` is keyed by the option's own `index` — the
 * api's number, and the one a vote sends back — so a caller never assumes the array order matches.
 *
 * `total` is the sum of the options' votes, which is the vote count a single-choice poll and a
 * multiple-choice one both mean: with `multiple` the same member can be counted under several
 * options, and each bar's width is its share of every vote cast.
 */
export function voteTally(poll: PollView): VoteTally {
  const perOption: number[] = []
  let total = 0
  for (const option of poll.options) {
    perOption[option.index] = option.voterIds.length
    total += option.voterIds.length
  }
  return { total, perOption }
}

/**
 * What stops this draft from being sent, as the dictionary key that says it — or null when the
 * draft is ready. The api's own order (`apps/api/src/messages.ts`) and its own bounds, so the
 * dialog never shows a reason the server would not give, and never sends a draft the server would
 * refuse: the question first (it is the field at the top), then the option count, then each option
 * in turn — its text trimmed, exactly as the api measures it.
 *
 * The dialog shows this while Send is disabled, which is the whole point: a disabled button that
 * says nothing is a dead end, and this is the one sentence that names the way out.
 */
export function pollDraftProblem(draft: Pick<PollDraft, 'question' | 'options'>): MessageKey | null {
  if (draft.question.trim() === '') return 'poll.errorQuestion'
  if (draft.options.length < POLL_MIN_OPTIONS || draft.options.length > POLL_MAX_OPTIONS) {
    return 'poll.errorOptionCount'
  }
  for (const option of draft.options) {
    const text = option.trim()
    if (text === '') return 'poll.errorOptionEmpty'
    if (text.length > POLL_OPTION_MAX_CHARS) return 'poll.errorOptionLong'
  }
  return null
}
